import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarClock, CreditCard } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { diasHasta, fecha } from '@/lib/fecha';
import { cn } from '@/lib/utils';

/**
 * Avisa de que la suscripción está por vencer, antes de que venza.
 *
 * El acceso se corta el día siguiente al vencimiento y no hay período de
 * gracia —así está en los términos—. Sin este aviso el cliente se entera el
 * día que no puede entrar, con el salón abierto y clientas esperando. Es el
 * peor momento para descubrir que había que pagar.
 *
 * Se muestra desde 7 días antes, que es el plazo que pidió el dueño: da
 * tiempo a organizar el pago sin convertirse en ruido permanente.
 *
 * Aparece en TODAS las pantallas, no solo en la de suscripción: quien va a
 * «Mi suscripción» ya sabe que tiene que pagar. Hay que alcanzar a quien no
 * va nunca.
 */

/** A partir de aquí se avisa. Lo fijó el dueño del negocio. */
const DIAS_DE_AVISO = 7;

export function AvisoDeVencimiento() {
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);

  // El superadministrador no tiene suscripción que vencer: avisarle de la
  // licencia de Netcloud en cada pantalla es ruido.
  if (!tenant || user?.isSuperAdmin) return null;

  // El vencimiento real es el de la licencia; la prueba se refleja ahí mismo.
  const vence = tenant.licenseUntil ?? tenant.trialEndsAt;
  if (!vence) return null;

  const dias = diasHasta(vence);
  if (dias === null) return null;

  const enPrueba = tenant.status === 'TRIAL';
  const vencido = dias < 0;

  // Fuera de la ventana de aviso no se muestra nada. Un banner permanente se
  // vuelve invisible justo cuando hace falta que se vea.
  if (!vencido && dias > DIAS_DE_AVISO) return null;

  const tono = vencido || dias <= 2
    ? 'border-rose-300 bg-rose-50 text-rose-900'
    : dias <= 4
      ? 'border-amber-300 bg-amber-50 text-amber-900'
      : 'border-sky-300 bg-sky-50 text-sky-900';

  const Icono = vencido ? AlertTriangle : CalendarClock;

  return (
    <div
      role="status"
      className={cn(
        'mb-4 flex flex-col gap-3 rounded-xl border px-4 py-3',
        'sm:flex-row sm:items-center sm:justify-between',
        tono,
      )}
    >
      <div className="flex items-start gap-3">
        <Icono className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="min-w-0 text-sm">
          <p className="font-semibold">{titulo(dias, vencido, enPrueba)}</p>
          <p className="mt-0.5 opacity-90">
            {enPrueba ? 'Tu prueba' : 'Tu plan'}{' '}
            {vencido ? 'venció el' : 'va hasta el'}{' '}
            <strong>{fecha(vence, "d 'de' MMMM")}</strong>
            {vencido
              ? '. Paga para recuperar el acceso.'
              : '. Paga antes de esa fecha para no quedarte por fuera.'}
          </p>
        </div>
      </div>

      <Link
        to="/subscription"
        className={cn(
          'inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-4 py-2',
          'text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90',
          vencido || dias <= 2 ? 'bg-rose-600' : dias <= 4 ? 'bg-amber-600' : 'bg-sky-600',
        )}
      >
        <CreditCard className="h-4 w-4" />
        {vencido ? 'Pagar ahora' : 'Pagar o renovar'}
      </Link>
    </div>
  );
}

/**
 * El encabezado dice el plazo en palabras. «Vence en 3 días» se entiende de
 * un vistazo; una fecha sola obliga a hacer la cuenta mentalmente.
 */
function titulo(dias: number, vencido: boolean, enPrueba: boolean): string {
  const qué = enPrueba ? 'Tu prueba' : 'Tu suscripción';
  if (vencido) {
    const d = Math.abs(dias);
    if (d === 0) return `${qué} vence hoy`;
    return `${qué} venció hace ${d} ${d === 1 ? 'día' : 'días'}`;
  }
  if (dias === 0) return `${qué} vence hoy`;
  if (dias === 1) return `${qué} vence mañana`;
  return `${qué} vence en ${dias} días`;
}
